import { useRef, useState } from "react";
import { CaretDown, FileText, ArrowsClockwise, Warning } from "@phosphor-icons/react";

const CARD_ORDER = [
  ["niche", "Niche"],
  ["clients", "Ideal Clients"],
  ["problem", "Problem"],
  ["outcome", "Outcome"],
  ["delivery", "Delivery"],
  ["economics", "Economics"],
  ["fit", "Fit"],
  ["reframe", "Reframe"],
];

const LABELS = {
  statement: "Positioning",
  why_fit: "Why it fits",
  summary: "Summary",
  industries: "Industries",
  company_size: "Company size",
  decision_maker: "Decision maker",
  geography: "Geography",
  client_voice_quote: "In the client's words",
  formats: "Formats",
  retainer: "Retainer",
  project: "Project",
  six_month_target: "6-month target",
  phases: "Phases",
  start_calling: "Start calling yourself",
};

export function formatDocDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function ListValue({ items }) {
  return (
    <ul className="niche-list">
      {items.map((v, i) => <li key={i}>{v}</li>)}
    </ul>
  );
}

function ObjectValue({ obj }) {
  const FIRST = ["statement", "summary"];
  const entries = Object.entries(obj)
    .filter(([, v]) => v && (!Array.isArray(v) || v.length))
    .sort(([a], [b]) => (FIRST.includes(b) ? 1 : 0) - (FIRST.includes(a) ? 1 : 0));
  return (
    <dl className="niche-dl">
      {entries.map(([k, v]) => (
        <div key={k}>
          <dt>{LABELS[k] || k}</dt>
          <dd>{Array.isArray(v) ? <ListValue items={v} /> : <p>{String(v)}</p>}</dd>
        </div>
      ))}
    </dl>
  );
}

function FieldValue({ value }) {
  if (Array.isArray(value)) return <ListValue items={value} />;
  if (value && typeof value === "object") return <ObjectValue obj={value} />;
  return <p>{String(value)}</p>;
}

export function NicheProfileCards({ profile, confidence, testIdPrefix = "niche" }) {
  const cards = CARD_ORDER.filter(([k]) => profile && profile[k]);
  if (!cards.length) return <p className="text-sm" style={{ color: "var(--muted)" }}>Nothing could be read from this document.</p>;
  return (
    <div className="niche-cards" data-testid={`${testIdPrefix}-cards`}>
      {cards.map(([key, label]) => {
        const level = confidence?.[key];
        const check = level === "low" || level === "medium";
        return (
          <div key={key} className={`niche-card ${check ? "is-check" : ""}`} data-testid={`${testIdPrefix}-card-${key}`}>
            <div className="niche-card-head">
              <span className="label-uppercase">{label}</span>
              {check && (
                <span className="niche-check-tag" data-testid={`${testIdPrefix}-check-${key}`}>
                  <Warning size={10} weight="bold" /> Please check this
                </span>
              )}
            </div>
            <FieldValue value={profile[key]} />
          </div>
        );
      })}
    </div>
  );
}

export default function NicheDocStrip({ doc, onReplace, busy }) {
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  if (!doc) return null;
  return (
    <div className={`niche-strip ${open ? "is-open" : ""}`} data-testid="niche-doc-strip">
      <div className="niche-strip-row">
        <button type="button" className="niche-strip-toggle" onClick={() => setOpen((o) => !o)} data-testid="niche-doc-toggle" aria-expanded={open}>
          <FileText size={16} weight="duotone" />
          <span>
            Working from your Niche Recommendation
            <span className="niche-strip-date"> · uploaded {formatDocDate(doc.uploaded_at)}</span>
            {doc.name_match === "borderline" && (
              <span className="niche-strip-note" data-testid="niche-doc-name-note"> · name looked slightly different — check it's yours</span>
            )}
          </span>
          <CaretDown size={14} weight="bold" className="niche-strip-caret" />
        </button>
        <button type="button" className="niche-replace-btn" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="niche-doc-replace">
          <ArrowsClockwise size={13} weight="bold" /> {busy ? "Reading…" : "Replace document"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          className="hidden"
          data-testid="niche-doc-replace-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) onReplace(f);
          }}
        />
      </div>
      {open && (
        <div className="niche-strip-body" data-testid="niche-doc-details">
          <NicheProfileCards profile={doc.profile} confidence={doc.confidence} />
        </div>
      )}
    </div>
  );
}
